import { GiftItem } from "../../types";
import { GiftGroup } from "../../types";
import { getMedianPrice } from "./getMedianPrice";
import { MultiPosGiftGroupsState } from "../../redux-store/reducers/gifts-reducers/multiPosGiftGroupsReducer";
import { GiftGroupsState } from "../../redux-store/reducers/gifts-reducers/giftGroupsReducer";
import { createPlaceholderItem } from "../../redux-store/reducers/gifts-reducers/utils";

type RemovePosGiftGroupItemParams = {
  state: MultiPosGiftGroupsState;
  group_id: string;
  itemIndex: number;
  currency: {
    en: string;
    ar: string;
  };
  pos_entity_id: string;
  isReference: boolean;
};

const updateGroupAfterItemRemoval = (
  group: GiftGroup,
  updatedItems: GiftItem[],
  currency: {
    en: string;
    ar: string;
  }
): GiftGroup => {
  const medianPrice = getMedianPrice(updatedItems);
  return {
    ...group,
    items: updatedItems,
    price: medianPrice,
    name: group.is_renamed
      ? group.name
      : `Gift - ${medianPrice} ${currency.en}`,
    ar_name: group.is_ar_renamed
      ? group.ar_name
      : `هدية - ${medianPrice} ${currency.ar}`,
  };
};

const handleEmptyGroup = (
  posState: GiftGroupsState,
  group_id: string
): GiftGroupsState => {
  const { [group_id]: _, ...restById } = posState.shownGiftGroupsById;
  return {
    ...posState,
    shownGiftGroupsById: restById,
    shownGiftGroupsIds: posState.shownGiftGroupsIds.filter(
      (id) => id !== group_id
    ),
  };
};

export const removePosGiftGroupItem = ({
  state,
  group_id,
  itemIndex,
  currency,
  pos_entity_id,
  isReference,
}: RemovePosGiftGroupItemParams): MultiPosGiftGroupsState["giftGroupsByPosId"] => {
  if (isReference) {
    return Object.entries(state.giftGroupsByPosId).reduce(
      (acc, [posId, posState]) => {
        const group = posState.shownGiftGroupsById[group_id];
        if (!group) return acc;

        const updatedItems = [...(group.items as GiftItem[])];
        updatedItems.splice(itemIndex, 1);

        const reorderedItems = updatedItems.map((item, idx) => ({
          ...item,
          order: idx + 1,
        }));

        if (reorderedItems.length === 0) {
          acc[posId] = handleEmptyGroup(posState, group_id);
          return acc;
        }

        const updatedGroup = updateGroupAfterItemRemoval(
          group,
          reorderedItems,
          currency
        );

        acc[posId] = {
          ...posState,
          shownGiftGroupsById: {
            ...posState.shownGiftGroupsById,
            [group_id]: updatedGroup,
          },
        };
        return acc;
      },
      {} as MultiPosGiftGroupsState["giftGroupsByPosId"]
    );
  }

  const currentState = state.giftGroupsByPosId[pos_entity_id];
  const group = currentState.shownGiftGroupsById[group_id];
  const items = group.items as GiftItem[];
  const updatedItems = [...items];
  const originalItem = updatedItems[itemIndex];
  updatedItems[itemIndex] = createPlaceholderItem(originalItem.price);

  const updatedGroup = updateGroupAfterItemRemoval(
    group,
    updatedItems,
    currency
  );

  return {
    ...state.giftGroupsByPosId,
    [pos_entity_id]: {
      ...currentState,
      shownGiftGroupsById: {
        ...currentState.shownGiftGroupsById,
        [group_id]: updatedGroup,
      },
    },
  };
};
